import Bugsnag from "@bugsnag/js";
import classNames from "classnames";
import { FunctionComponent, useState } from "react";
import { useIntl } from "react-intl";
import ButtonSmily from "@Components/atoms/ButtonSmily";
import Tag from "@Components/atoms/Tag";
import BottomSheet from "@Components/molecules/BottomSheet";
import ChoicesPacks from "@Components/molecules/choices/ChoicesPacks";
import Panel from "@Components/molecules/Panel";
import { useBreakpoint } from "@Hooks/useBreakpoint";
import { ReactComponent as IconArrowLeft } from "@Icons/16x16/arrow-left.svg";
import { ReactComponent as IconFrownHead } from "@Icons/16x16/frown-head.svg";
import { Section, Tile } from "@Types/pagify";
import { getLink } from "@Utils/pagify";
import "./style.scss";

export const ChoicesCards: FunctionComponent<{
  section: Section;
  tiles: Tile[];
}> = ({ section, tiles }) => {
  const intl = useIntl();
  const locale = intl.locale;

  const { isLargeScreen } = useBreakpoint();

  const [selectedChoice, setSelectedChoice] = useState<Tile>(tiles[0]);
  const [displayPacks, setDiplayPacks] = useState<boolean>(false);

  const handleClick = (tile: Tile) => {
    setSelectedChoice(tile);
    if (tile.action && tile.action.entries && tile.action.entries.length > 0) {
      setDiplayPacks(true);
    } else {
      setDiplayPacks(false);
      const link = getLink(tile.navigationLink, locale);
      if (!!link) {
        location.href = link;
      }
    }
  };

  const handleDismiss = () => {
    setDiplayPacks(false);
    document.body.classList.toggle("noScroll", false);
  };

  return (
    <div className="ChoicesCards">
      <div className="ChoicesCards-back">
        <ButtonSmily
          icon={<IconArrowLeft />}
          iconPosition="left"
          onClick={() => window.history.back()}
          size="s"
          type="tertiary"
        >
          {intl.formatMessage({ id: "cheerz_LoginModal_back" })}
        </ButtonSmily>
      </div>
      <div className="ChoicesCards-title">{section.title}</div>
      <div className="ChoicesCards-wrapper">
        {tiles.map((tile) => {
          const image = tile.images.length > 0 && tile.images[0].desktop;
          const name = tile.textBlock1 && tile.textBlock1[0].text;
          const price = tile.textBlock2 && tile.textBlock2[0].text;
          const isOutOfStock = tile.metadata?.availability === "out_of_stock";

          if (!image) {
            Bugsnag.notify(new Error(`image for tile ${tile.slug} not found in ChoicesCards`));
            return null;
          }

          return (
            <div
              className={classNames("ChoicesCards-item", {
                "ChoicesCards-item--outOfStock": isOutOfStock,
              })}
              key={tile.slug}
              onClick={() => handleClick(tile)}
            >
              <img
                alt={name || tile.slug}
                className="ChoicesCards-item-image"
                height={248}
                src={image["1x"]}
                srcSet={`${image["1x"]} 1x, ${image["2x"]} 2x`}
                width={248}
              />

              {isOutOfStock && (
                <div className="ChoicesCards-item-outOfStock">
                  <Tag backgroundColor="#868D98" isRotated={true} size="m">
                    <IconFrownHead />
                    {intl.formatMessage({ id: "cheerz_Drafts_out_of_stock" })}
                  </Tag>
                </div>
              )}

              <div className="ChoicesCards-item-description">
                <div className="ChoicesCards-item-description-text">{name}</div>
                <div className="ChoicesCards-item-description-price">{price}</div>
              </div>
            </div>
          );
        })}
      </div>
      {isLargeScreen ? (
        <Panel display={displayPacks} onDismiss={handleDismiss}>
          <ChoicesPacks tile={selectedChoice} />
        </Panel>
      ) : (
        <BottomSheet display={displayPacks} onDismiss={handleDismiss} showCloseButton={true}>
          <ChoicesPacks tile={selectedChoice} />
        </BottomSheet>
      )}
    </div>
  );
};
